import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Dialog,
    TablePagination,
    IconButton,
    Tooltip,
    Stack,
    Chip
} from '@mui/material';
import { FetchRoles, FetchOrganizations, DeleteRole } from '../../../../services/api';
import CreateRoleForm from './CreateRoleForm';
import usePermission from '../../../../hooks/usePermission';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const RolesList = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editData, setEditData] = useState(null);
    const [orgMap, setOrgMap] = useState({});

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const token = localStorage.getItem('accessToken');
    const { can } = usePermission();

    const canCreate = can('role.create');
    const canUpdate = can('role.update');
    const canDelete = can('role.delete');

    useEffect(() => {
        loadOrgs();
    }, []);

    useEffect(() => {
        loadRoles();
    }, [page, rowsPerPage]);

    const loadOrgs = async () => {
        try {
            // Fetch all organizations for mapping (limit 1000 should cover list)
            const response = await FetchOrganizations(token, { limit: 1000 });
            if (response?.data) {
                const orgs = Array.isArray(response.data) ? response.data : (response.data.data || []);
                const map = {};
                orgs.forEach(org => {
                    const id = org.id || org._id;
                    map[id] = org.organizationName;
                });
                setOrgMap(map);
            }
        } catch (error) {
            console.error("Failed to load organizations for mapping", error);
        }
    };

    const loadRoles = async () => {
        setLoading(true);
        try {
            const response = await FetchRoles(token, {
                page: page + 1,
                limit: rowsPerPage
            });

            if (response?.data?.roles) {
                setRoles(response.data.roles);
                if (response.data.pagination) {
                    setTotalCount(response.data.pagination.total);
                } else {
                    setTotalCount(response.data.roles.length);
                }
            } else if (Array.isArray(response?.data)) {
                setRoles(response.data);
                setTotalCount(response.data.length);
            }
        } catch (error) {
            console.error("Failed to fetch roles", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page
    };

    const handleCreate = () => {
        setEditData(null);
        setOpenDialog(true);
    };

    const handleEdit = (role) => {
        setEditData(role);
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this role?")) {
            try {
                const response = await DeleteRole(id, token);
                if (response?.status === 200 || response?.data?.message === 'Role deleted successfully') {
                    loadRoles();
                } else {
                    alert("Failed to delete role. It may have assigned users.");
                }
            } catch (error) {
                console.error("Delete failed", error);
                alert("An error occurred during deletion.");
            }
        }
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditData(null);
    };

    const handleSuccess = () => {
        loadRoles();
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">Role Management</Typography>
                {canCreate && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        Create Role
                    </Button>
                )}
            </Box>

            <Paper elevation={2}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Organization</strong></TableCell>
                                <TableCell><strong>Category</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : roles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="textSecondary">No roles found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                roles.map((role) => (
                                    <TableRow key={role.id || role._id} hover>
                                        <TableCell>{role.name}</TableCell>
                                        <TableCell>{role.description}</TableCell>
                                        <TableCell>
                                            {/* Resolution: 1. role.organization obj, 2. orgMap lookup, 3. '-' */}
                                            {role.organization?.organizationName || orgMap[role.organizationId] || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={role.roleCategory} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={role.isActive ? 'Active' : 'Inactive'}
                                                color={role.isActive ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                {canUpdate && (
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleEdit(role)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDelete(role.id || role._id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog
                open={openDialog}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, height: '90vh' } }} // Adjusted height for Role form
            >
                <CreateRoleForm
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                    initialData={editData}
                />
            </Dialog>
        </Box>
    );
};

export default RolesList;
